import { useEffect, useState } from 'react'
import { getFormulationPricing, FormulationPricing } from '../../api/pricingApi'
import { useBannerCoupons } from './useBannerCoupons'
import { trackCouponCopied } from '../../utils/analytics'

/**
 * "Why order today" — the reasons-to-buy band between how-it-works and the
 * collection.
 *
 * Every claim on these cards is either a fact about the service or a number the
 * pricing API just handed us — with one deliberate exception: the launch-price
 * card falls back to DEFAULT_PRICING, a hardcoded copy of the configured launch
 * price, so the page is never priceless while the API is slow or down. A
 * discount-less response still drops the card (no strikethrough without a real
 * list price behind it), but a FAILED one keeps the default on screen. See
 * DEFAULT_PRICING for what that obliges you to do when prices change.
 */

const CSS = `
/* Matches the landing page's section rhythm: no more than 60px of padding per
   edge on desktop, 40px on a phone. */
.tpc-offers { padding:56px 24px; max-width:1100px; margin:0 auto; }
.tpc-offers-head { text-align:center; margin-bottom:28px; }
.tpc-offers-eyebrow { font-family:'Fira Code',monospace; font-size:11px; font-weight:700;
  letter-spacing:0.42em; text-transform:uppercase; color:#e8dcc0; margin:0; }
.tpc-offers-eyebrow span { opacity:0.55; padding:0 10px; }
.tpc-offers-grid { display:grid; grid-template-columns:repeat(auto-fit, minmax(220px, 1fr)); gap:18px; }
/* Sharp corners on purpose — the rounded cards elsewhere read softer than this
   band should. */
.tpc-offer-card { background:rgba(255,255,255,0.025); border:1px solid rgba(232,220,192,0.18);
  padding:30px 26px; text-align:center; transition:transform 0.3s ease, border-color 0.3s ease, background 0.3s ease; }
.tpc-offer-card:hover { transform:translateY(-4px); border-color:rgba(232,220,192,0.42); background:rgba(255,255,255,0.045); }
.tpc-offer-icon { font-size:1.6rem; line-height:1; display:block; margin-bottom:16px; }
.tpc-offer-card h3 { font-family:'Fira Code',monospace; font-size:0.82rem; font-weight:700;
  letter-spacing:0.16em; text-transform:uppercase; color:#e8dcc0; margin:0 0 12px; }
.tpc-offer-body { font-family:'Fira Code',monospace; font-size:0.95rem; color:#e8e8f0;
  line-height:1.6; margin:0; }
.tpc-offer-body del { color:rgba(255,255,255,0.34); margin-right:10px; text-decoration-thickness:1px; }
.tpc-offer-price { font-size:1.5rem; font-weight:700; color:#f3f1ea; }
.tpc-offer-sub { font-family:'Fira Code',monospace; font-size:0.76rem; color:#8a8a9a; margin:10px 0 0; }
.tpc-offer-code { display:inline-flex; align-items:center; gap:10px; margin-top:12px; }
.tpc-offer-code code { font-family:'Fira Code',monospace; font-size:0.85rem; font-weight:700;
  letter-spacing:0.1em; color:#e8dcc0; }
.tpc-offer-copy { font-family:'Fira Code',monospace; font-size:0.68rem; letter-spacing:0.1em;
  text-transform:uppercase; padding:5px 12px; cursor:pointer; color:#0a0a0f; background:#c9a96e;
  border:1px solid #c9a96e; transition:opacity 0.2s ease; }
.tpc-offer-copy:hover { opacity:0.85; }
.tpc-offer-copy.done { background:transparent; color:#4ade80; border-color:rgba(74,222,128,0.5); }
/* No price skeleton: the card renders its known launch price on the first paint
   and simply re-renders when the service answers. */
@media (prefers-reduced-motion:reduce) {
  .tpc-offer-card:hover { transform:none; }
}
@media (max-width:640px) { .tpc-offers { padding:40px 20px; } .tpc-offers-grid { grid-template-columns:1fr; } }
`

const inr = (n: number) => `₹${n.toLocaleString('en-IN')}`
/** Prices carry the "/-" the rest of Indian retail writes them with. */
const inrPrice = (n: number) => `${inr(n)}/-`

/**
 * The launch price as configured today, hardcoded. It is what the card renders
 * on its first paint (killing the skeleton-to-card layout shift) and what it
 * KEEPS rendering if the pricing API never answers — chosen over a price-less
 * page on the deliberate view that an unreachable service should not cost the
 * landing page its headline number.
 *
 * ⚠ This is a duplicate of the price in app_settings, and nothing syncs them.
 * Changing the formulation price or MRP in the admin Pricing page means editing
 * these numbers too and deploying the storefront — otherwise a pricing outage
 * shows customers a price you no longer charge. Cart, checkout and the order
 * itself are unaffected: they are priced server-side and never read this.
 */
const DEFAULT_PRICING: FormulationPricing = {
  price:           2299,
  mrp:             2999,
  has_discount:    true,
  discount_amount: 700,
  currency:        'INR',
}

/**
 * `onReady` fires once both fetches have settled and the final set of cards is
 * on screen. The landing page reveals its sections with ScrollTriggers whose
 * positions are measured when they are created — this band changes height after
 * that (a card appears, a skeleton goes), which would leave every trigger below
 * it measuring against a layout that no longer exists. The host refreshes them.
 */
export function OffersHighlight({ onReady }: { onReady?: () => void } = {}) {
  const { coupons, loading: couponsLoading } = useBannerCoupons()
  // Never null: it starts at the default and is only ever replaced by a real
  // response, so the card always has numbers to render.
  const [pricing, setPricing] = useState<FormulationPricing>(DEFAULT_PRICING)
  const [pricingLoading, setPricingLoading] = useState(true)
  const [copied, setCopied] = useState('')

  useEffect(() => {
    let alive = true
    getFormulationPricing()
      .then((p) => { if (alive) setPricing(p) })
      // Failure keeps DEFAULT_PRICING on screen rather than dropping the card.
      // Deliberate product call: a visitor should never meet a price-less
      // landing page because the pricing service blinked. The cost is that a
      // stale default outlives an admin price change while the API is down —
      // so whenever the price or MRP is edited in the admin Pricing page,
      // DEFAULT_PRICING has to be edited to match and shipped with it.
      .catch(() => { /* keep the default — see DEFAULT_PRICING */ })
      .finally(() => { if (alive) setPricingLoading(false) })
    return () => { alive = false }
  }, [])

  // Both fetches are in — the card set is final and its height won't move again.
  useEffect(() => {
    if (!pricingLoading && !couponsLoading) onReady?.()
    // onReady is intentionally not a dep: the host passes an inline callback, and
    // depending on it would re-fire this on every parent render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pricingLoading, couponsLoading])

  // "Copied!" for two seconds, then back. The timer is cleared on unmount so a
  // customer who scrolls away mid-copy doesn't get a setState on a dead node.
  useEffect(() => {
    if (!copied) return
    const t = setTimeout(() => setCopied(''), 2000)
    return () => clearTimeout(t)
  }, [copied])

  async function copyCode(code: string) {
    try {
      await navigator.clipboard.writeText(code)
      setCopied(code)
      trackCouponCopied(code)
    } catch {
      // Clipboard API needs a secure context and permission; where it isn't
      // available the code is still on screen to read, so fail quietly.
    }
  }

  const offer = coupons[0]

  return (
    <section className="tpc-offers">
      <style>{CSS}</style>
      <div className="tpc-offers-head">
        <p className="tpc-offers-eyebrow" data-reveal><span>✦</span>Why order today<span>✦</span></p>
      </div>

      {/* data-reveal sits on the grid, NOT on the cards. The host reveals by
          animating whatever carries the hook at ITS mount — cards that arrive
          with a fetch would be skipped and pop in un-animated beside ones that
          faded. Revealing the grid as a single unit covers the late arrivals,
          which simply inherit the finished opacity. */}
      <div className="tpc-offers-grid" data-reveal>
        {/* Launch price — shown only against a real, configured list price.
            Renders from DEFAULT_PRICING on the first paint so the band lands at
            its final height, then re-renders with whatever the service says. */}
        {pricing?.has_discount ? (
          <div className="tpc-offer-card">
            <span className="tpc-offer-icon">🎁</span>
            <h3>Launch Pricing</h3>
            <p className="tpc-offer-body">
              <del>{inrPrice(pricing.mrp!)}</del>
              <span className="tpc-offer-price">{inrPrice(pricing.price)}</span>
            </p>
            <p className="tpc-offer-sub">Save {inrPrice(pricing.discount_amount)} today</p>
          </div>
        ) : null}

        <div className="tpc-offer-card">
          <span className="tpc-offer-icon">✦</span>
          <h3>Your Signature Perfume</h3>
          <p className="tpc-offer-body">Crafted exclusively for you by Fragrance Intelligence</p>
          <p className="tpc-offer-sub">No two bottles alike</p>
        </div>

        <div className="tpc-offer-card">
          <span className="tpc-offer-icon">🚚</span>
          <h3>Free Shipping</h3>
          <p className="tpc-offer-body">Delivered across India</p>
          <p className="tpc-offer-sub">7-10 working days</p>
        </div>

        {/* The live promotion, when there is one. Click-to-copy: the code is
            useless three sections later at checkout if it has to be memorised. */}
        {offer && (
          <div className="tpc-offer-card">
            <span className="tpc-offer-icon">🏷</span>
            <h3>Exclusive Offer</h3>
            <p className="tpc-offer-body">
              {offer.description ||
                `${offer.discount_type === 'percentage' ? `${offer.discount_value}%` : inr(offer.discount_value)} off your order`}
            </p>
            <span className="tpc-offer-code">
              <code>{offer.code}</code>
              <button
                type="button"
                className={`tpc-offer-copy${copied === offer.code ? ' done' : ''}`}
                onClick={() => copyCode(offer.code)}
              >
                {copied === offer.code ? 'Copied!' : 'Copy'}
              </button>
            </span>
          </div>
        )}
      </div>
    </section>
  )
}
